import { Box, Button, Stack, Typography, alpha } from "@mui/material";
import { EditNote as EditNoteIcon } from "@mui/icons-material";
import { CompactTable, CompactTableColumn } from "@/components/common/CompactTable";
import { ClaimLinesPanel } from "./ClaimLinesPanel";
import {
  ClaimantProjection,
  ClaimItemRowForecast,
  ExtractedRecord,
  ReimbursementRowForecast,
  TargetSchema,
  ValidAny,
  type PolicyWindows,
} from "@/types";
import { useAppTheme } from "@/hooks/useAppTheme";
import { SCHEMA_LABELS } from "../../utils/reviewUtils";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { getRecordData, getRecordSchemas, getUnusedData } from "../../utils/recordUtils";
import { buildDynamicColumns, getSuppressedFieldsForData } from "./reviewGridColumns";
import { ReimbursementRowStateChip } from "./ReimbursementRowStateChip";
import { ClaimItemRowStateChip } from "./ClaimItemRowStateChip";
import { renderLineItemsExpand } from "./reimbursementReviewHelpers";
import { gridRowId } from "../../utils/recordIndexing";

/**
 * True when `record` has at least one non-empty section worth rendering in the
 * expand panel — overflow fields, cross-schema fields, unused_data, or linked
 * records from `linkedRecords`. Callers (e.g. the grid's `isExpandable` prop)
 * use this to suppress the expand chevron on rows where the panel would be
 * empty.
 *
 * Mirrors the early-return logic in `ExpandedRecordDetails` so a chevron only
 * appears when there's something meaningful behind it.
 */
export const hasExpandableContent = (
  record: ExtractedRecord,
  parentSchema: TargetSchema,
  shownFieldKeys: Set<string>,
  linkedRecords: ExtractedRecord[],
  nestedSchemas: TargetSchema[]
): boolean => {
  const parentData = getRecordData(record, parentSchema);
  const suppressed = getSuppressedFieldsForData(parentData);
  const hasOverflow = Object.entries(parentData).some(
    ([key, value]) =>
      !shownFieldKeys.has(key) &&
      !suppressed.has(key) &&
      !key.startsWith("__") &&
      value !== null &&
      value !== undefined &&
      value !== "" &&
      !(typeof value === "object" && Object.keys(value as Record<string, ValidAny>).length === 0)
  );
  if (hasOverflow) return true;

  if (groupCrossSchemaFields(record, parentSchema, shownFieldKeys).length > 0) return true;

  const hasUnused = Object.values(getUnusedData(record)).some((v) => v !== null && v !== undefined && v !== "");
  if (hasUnused) return true;

  const linkedBySchema = groupLinkedRecordsBySchema(linkedRecords, nestedSchemas);
  return nestedSchemas.some((s) => (linkedBySchema[s]?.length ?? 0) > 0);
};

interface ExpandedRecordDetailsProps {
  /** The parent (top-row) record whose details are being shown */
  record: ExtractedRecord;
  /** The top-row schema — used to surface non-priority fields of the parent record */
  parentSchema: TargetSchema;
  /** Fields already shown as columns on the parent row (hidden here to avoid duplication) */
  shownFieldKeys: Set<string>;
  /** Records linked to the parent — grouped by schema and rendered as nested tables */
  linkedRecords: ExtractedRecord[];
  /** Only show linked records from these schemas (per the document-type grouping strategy) */
  nestedSchemas: TargetSchema[];
  /** Opens the field mapping dialog. If undefined, the "Fix in field mapping" CTA is hidden. */
  onOpenFieldMapping?: () => void;
  /** Disables the field-mapping CTA (e.g. when client is unassigned). */
  isFieldMappingDisabled?: boolean;
  /** Looks up the per-row reimbursement forecast for a given record. Drives
   *  the row-level "On file" marker in nested reimbursement tables so the
   *  reviewer can tell which row will be created and which is a re-upload of
   *  an already-loaded reimbursement. */
  getReimbursementRowForRecord?: (record: ExtractedRecord) => ReimbursementRowForecast | undefined;
  /** Looks up the per-row claim_item forecast for a given record. Drives the
   *  "Insert" / "On file" marker in nested claim-item tables (AB#4456). */
  getClaimItemRowForRecord?: (record: ExtractedRecord) => ClaimItemRowForecast | undefined;
  /**
   * Claimant-panel context (#7435): when the parent is a claimant and the
   * nested schema is claim lines, render ClaimLinesPanel (fact line + flat
   * Service date · Paid date · Procedure · Paid · Source list) instead of the
   * generic nested table.
   */
  claimantProjection?: ClaimantProjection;
  onViewSource?: (record: ExtractedRecord) => void;
  sourceType?: "spreadsheet" | "pdf";
  /** Forwarded to ClaimLinesPanel (#7436): the policy's paid/incurred windows and whether the rail's "Dates are right" was clicked. */
  policyWindows?: PolicyWindows | null;
  outsidePolicyAcknowledged?: boolean;
  /** Forwarded to ClaimLinesPanel: reviewer exclusions, so excluded lines read as such. */
  isExcluded?: (record: ExtractedRecord) => boolean;
  /** Forwarded to nested tables — when true, populated non-critical columns
   *  surface alongside the critical column block (mirrors the parent grid's
   *  "Show all columns" toggle). Defaults to false (critical-only). */
  showAllColumns?: boolean;
}

/**
 * Content shown inside `CompactTable`'s expand panel for a top-row record.
 *
 * Layout, top-to-bottom:
 *   1. "Other fields" — parent record's non-priority fields (spill-over).
 *   2. Cross-schema fields on the SAME record — one section per non-parent
 *      schema. Covers mixed-row file shapes (e.g. Cigna Weekly Rx) where a
 *      single extracted record carries both claimant and claim_item fields;
 *      without this, reviewers would see the same record in a separate tab
 *      with no clear link between perspectives.
 *   3. "Not mapped" — record.unused_data with a "Fix in field mapping" CTA.
 *   4. Linked-record tables — records from OTHER extracted entries grouped by
 *      nested schema (claims, line items, reimbursements). This is the
 *      hierarchical-file path.
 */
export const ExpandedRecordDetails = ({
  record,
  parentSchema,
  shownFieldKeys,
  linkedRecords,
  nestedSchemas,
  claimantProjection,
  onViewSource,
  sourceType,
  onOpenFieldMapping,
  isFieldMappingDisabled,
  getReimbursementRowForRecord,
  getClaimItemRowForRecord,
  showAllColumns = false,
  policyWindows,
  outsidePolicyAcknowledged,
  isExcluded,
}: ExpandedRecordDetailsProps) => {
  const parentData = getRecordData(record, parentSchema);
  // Apply the same composite suppression as the grid columns so constituent
  // fields (e.g. claimant_first_name / claimant_last_name) don't reappear here
  // as a redundant "Other Fields" row when their composite (claimant_name) is
  // already rendered as a column.
  const suppressed = getSuppressedFieldsForData(parentData);
  const overflowEntries = Object.entries(parentData).filter(
    ([key, value]) =>
      !shownFieldKeys.has(key) &&
      !suppressed.has(key) &&
      !key.startsWith("__") &&
      value !== null &&
      value !== undefined &&
      value !== "" &&
      !(typeof value === "object" && Object.keys(value as Record<string, ValidAny>).length === 0)
  );

  // Cross-schema fields on the same record, keyed by non-parent schema.
  // For hierarchical files (one claimant header + separate claim_item rows) this
  // will be empty — children live in separate records and surface via linkedRecords.
  // Values already shown on the main row are filtered out so we don't render
  // visual duplicates of column values.
  const crossSchemaGroups = groupCrossSchemaFields(record, parentSchema, shownFieldKeys);

  const unusedEntries = Object.entries(getUnusedData(record)).filter(
    ([, value]) => value !== null && value !== undefined && value !== ""
  );

  const linkedBySchema = groupLinkedRecordsBySchema(linkedRecords, nestedSchemas);
  const hasAnyLinked = nestedSchemas.some((s) => (linkedBySchema[s]?.length ?? 0) > 0);

  if (overflowEntries.length === 0 && crossSchemaGroups.length === 0 && unusedEntries.length === 0 && !hasAnyLinked) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        No additional details to show.
      </Typography>
    );
  }

  // When the row has nested records (e.g. claim_items under a claimant), the
  // nested table already shows that data row-by-row. Re-rendering "Other fields"
  // or cross-schema groups above it just duplicates the same numbers in a
  // less-readable shape — for itemized docs (Cigna MedicalDetail etc.) the
  // parent record carries flattened claim-item fields that surface here as
  // noise. Suppress those sections when nested data is present; keep the
  // "Not mapped" section because it's actionable (open Field Mapping).
  const hideOverflowSections = hasAnyLinked;

  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      {!hideOverflowSections && overflowEntries.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
            Other fields
          </Typography>
          <Box
            sx={{
              mt: 0.75,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              columnGap: 3,
              rowGap: 0.5,
            }}
          >
            {overflowEntries.map(([key, value]) => (
              <Box key={key} sx={{ display: "flex", gap: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>
                  {formatToTitleCase(key)}
                </Typography>
                <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {formatValue(value)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {!hideOverflowSections &&
        crossSchemaGroups.map(({ schema, entries }) => (
          <CrossSchemaSection key={schema} schema={schema} entries={entries} />
        ))}

      {unusedEntries.length > 0 && (
        <NotMappedSection
          entries={unusedEntries}
          onOpenFieldMapping={onOpenFieldMapping}
          isFieldMappingDisabled={isFieldMappingDisabled}
        />
      )}

      {nestedSchemas.map((schema) => {
        const linked = linkedBySchema[schema] ?? [];
        if (linked.length === 0) return null;
        if (parentSchema === TargetSchema.CLAIMANT && schema === TargetSchema.CLAIM_ITEM) {
          return (
            <ClaimLinesPanel
              key={schema}
              parentRecord={record}
              lines={linked}
              projection={claimantProjection}
              getClaimItemRowForRecord={getClaimItemRowForRecord}
              onViewSource={onViewSource}
              sourceType={sourceType}
              policyWindows={policyWindows}
              outsidePolicyAcknowledged={outsidePolicyAcknowledged}
              isExcluded={isExcluded}
            />
          );
        }
        return (
          <NestedRecordsTable
            key={schema}
            schema={schema}
            records={linked}
            getReimbursementRowForRecord={getReimbursementRowForRecord}
            getClaimItemRowForRecord={getClaimItemRowForRecord}
            showAllColumns={showAllColumns}
          />
        );
      })}
    </Stack>
  );
};

// ─── Cross-schema section ───────────────────────────────────────────────────

interface CrossSchemaSectionProps {
  schema: TargetSchema;
  entries: [string, ValidAny][];
}

/**
 * Renders the parent record's fields that belong to a different target schema
 * — e.g. the claim_item fields on a Weekly Rx row where the claimant and the
 * claim item are both extracted into the same record. Uses the same
 * key/value grid shape as "Other fields" so the visual treatment stays
 * consistent; only the heading differs.
 */
const CrossSchemaSection = ({ schema, entries }: CrossSchemaSectionProps) => (
  <Box>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
      {SCHEMA_LABELS[schema]} details ({entries.length})
    </Typography>
    <Box
      sx={{
        mt: 0.75,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
        columnGap: 3,
        rowGap: 0.5,
      }}
    >
      {entries.map(([key, value]) => (
        <Box key={key} sx={{ display: "flex", gap: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>
            {formatToTitleCase(key)}
          </Typography>
          <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {formatValue(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

// ─── Not mapped ─────────────────────────────────────────────────────────────

interface NotMappedSectionProps {
  entries: [string, ValidAny][];
  onOpenFieldMapping?: () => void;
  isFieldMappingDisabled?: boolean;
}

const NotMappedSection = ({ entries, onOpenFieldMapping, isFieldMappingDisabled }: NotMappedSectionProps) => {
  const { theme } = useAppTheme();
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 0.75 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
            Not mapped ({entries.length})
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic" }}>
            Detected in the source but not mapped to any target field.
          </Typography>
        </Box>
        {onOpenFieldMapping && (
          <Button
            size="small"
            variant="text"
            startIcon={<EditNoteIcon fontSize="small" />}
            onClick={onOpenFieldMapping}
            disabled={isFieldMappingDisabled}
          >
            Fix in field mapping
          </Button>
        )}
      </Box>
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: alpha(theme.palette.warning.main, 0.04),
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
          columnGap: 3,
          rowGap: 0.5,
          px: 1.5,
          py: 1,
        }}
      >
        {entries.map(([key, value]) => (
          <Box key={key} sx={{ display: "flex", gap: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120, flexShrink: 0 }}>
              {formatToTitleCase(key)}
            </Typography>
            <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {formatValue(value)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatValue = (value: ValidAny): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

/**
 * Groups a record's extracted_fields by target_schema, excluding the parent
 * schema (already rendered as columns + "Other fields").
 *
 * Filters out entries whose value already appears in one of the row's shown
 * columns — common for 50%-style reports where the same carrier total gets
 * mapped to both `claimant.ytd_total_paid` (shown as a column) and
 * `claim_item.paid_amount`. Without this filter the expand panel shows
 * "Claims details (1): Paid Amount $255,753" that's visually identical to the
 * row's Ytd Total Paid column — pure noise.
 *
 * Returns schemas in a stable order (first appearance in extracted_fields)
 * so the same document renders consistently across reloads.
 */
const groupCrossSchemaFields = (
  record: ExtractedRecord,
  parentSchema: TargetSchema,
  shownFieldKeys: Set<string>
): { schema: TargetSchema; entries: [string, ValidAny][] }[] => {
  const buckets = new Map<TargetSchema, [string, ValidAny][]>();
  const order: TargetSchema[] = [];

  // Values that are already rendered as columns on the main row — strings so
  // we can compare against any primitive after normalizing.
  const redundantValues = collectShownColumnValues(record, parentSchema, shownFieldKeys);

  for (const field of record.extracted_fields ?? []) {
    if (field.target_schema === parentSchema) continue;
    const value = field.extracted_value;
    if (value === null || value === undefined || value === "") continue;
    if (redundantValues.has(normalizeForComparison(value))) continue;

    if (!buckets.has(field.target_schema)) {
      buckets.set(field.target_schema, []);
      order.push(field.target_schema);
    }
    buckets.get(field.target_schema)!.push([field.target_field, value]);
  }

  return order.map((schema) => ({ schema, entries: buckets.get(schema) ?? [] })).filter((g) => g.entries.length > 0);
};

/** Build the set of stringified values shown on the main row's columns so we
 *  can filter cross-schema entries that would visually duplicate them. */
const collectShownColumnValues = (
  record: ExtractedRecord,
  parentSchema: TargetSchema,
  shownFieldKeys: Set<string>
): Set<string> => {
  const values = new Set<string>();
  const parentData = getRecordData(record, parentSchema);
  for (const key of shownFieldKeys) {
    const v = parentData[key];
    if (v === null || v === undefined || v === "") continue;
    values.add(normalizeForComparison(v));
  }
  return values;
};

/** Normalize primitives for value-equality comparison. Numbers and numeric
 *  strings collapse to the same key so `255753.01` (number) matches
 *  `"255753.01"` (string). */
const normalizeForComparison = (v: ValidAny): string => {
  if (typeof v === "number") return String(v);
  if (typeof v === "string") {
    const n = Number(v);
    if (Number.isFinite(n) && v.trim() !== "") return String(n);
    return v;
  }
  return String(v);
};

const groupLinkedRecordsBySchema = (
  records: ExtractedRecord[],
  nestedSchemas: TargetSchema[]
): Partial<Record<TargetSchema, ExtractedRecord[]>> => {
  const nested = new Set(nestedSchemas);
  const grouped: Partial<Record<TargetSchema, ExtractedRecord[]>> = {};
  for (const record of records) {
    for (const schema of getRecordSchemas(record)) {
      if (!nested.has(schema)) continue;
      if (!grouped[schema]) grouped[schema] = [];
      grouped[schema]!.push(record);
    }
  }
  return grouped;
};

// ─── Nested table ───────────────────────────────────────────────────────────

interface NestedRecordsTableProps {
  schema: TargetSchema;
  records: ExtractedRecord[];
  getReimbursementRowForRecord?: (record: ExtractedRecord) => ReimbursementRowForecast | undefined;
  getClaimItemRowForRecord?: (record: ExtractedRecord) => ClaimItemRowForecast | undefined;
  showAllColumns?: boolean;
}

// Each linked record needs a stable synthetic id for CompactTable.
//
// SHARED with RecordReviewGrid via gridRowId, and that sharing is the fix
// rather than tidying. This had its own WeakMap and `nested_N` counter, and
// both assigners mutate the record with Object.assign — so rendering this
// table overwrote the PARENT row's id in the outer grid, whose expand state
// was keyed on the old one. A claims-register row carries both claimant and
// claim_item fields, so a claimant's linked set contains the claimant itself,
// which is how the parent ended up in its own nested table. See gridRowId.
const asNestedRow = (record: ExtractedRecord): ExtractedRecord & { id: string } =>
  Object.assign(record, { id: gridRowId(record) }) as ExtractedRecord & { id: string };

// Sort rank for the claim-item nested table: New lines (0) sort before
// already-on-file lines (1), so the reviewer sees what's being added first.
const claimItemSortRank = (fc: ClaimItemRowForecast | undefined): number =>
  fc && (fc.operation === "update" || fc.is_duplicate_on_file) ? 1 : 0;

const NestedRecordsTable = ({
  schema,
  records,
  getReimbursementRowForRecord,
  getClaimItemRowForRecord,
  showAllColumns = false,
}: NestedRecordsTableProps) => {
  // Drop cross-reference columns (e.g. Claimant name) — the parent row already
  // identifies them, so repeating the value on every nested row is pure noise.
  // Column policy C drives the rest: by default only the schema's critical
  // columns appear, so the nested table matches what the standalone Claims /
  // Reimbursements tab shows. Wide tables scroll horizontally inside their
  // own CompactTable Paper.
  //
  // Tighten column widths for the nested context: replace any `Nfr` flex unit
  // with `max-content` so text columns size to their actual content rather
  // than stretching to fill the panel width. Empty space on the right of a
  // narrow nested table is preferable to a single column ballooning to gulp
  // it. Fixed-px widths (currency/date/id columns + the row-state column)
  // pass through unchanged.
  const dynamicColumns = buildDynamicColumns(records, schema, { showAllColumns })
    .filter((c) => !c.key.startsWith("__xref_"))
    .map((c) => ({ ...c, width: c.width.replace(/(\d+(?:\.\d+)?)fr/g, "max-content") })) as CompactTableColumn<
    ExtractedRecord & { id: string }
  >[];
  // On the claim-item nested table, surface NEW lines first so a reviewer doesn't
  // have to page through on-file lines to find what's actually being added.
  // Stable sort (New → On file) preserves document order within each group. Only
  // reorders the claim-item table; other nested schemas keep document order. AB#4456.
  const orderedRecords =
    schema === TargetSchema.CLAIM_ITEM && getClaimItemRowForRecord
      ? [...records].sort(
          (a, b) => claimItemSortRank(getClaimItemRowForRecord(a)) - claimItemSortRank(getClaimItemRowForRecord(b))
        )
      : records;
  const rows = orderedRecords.map(asNestedRow);

  // Reimbursement-only: when the projection has loaded forecasts for these
  // rows, prepend a Status column showing each row's disposition (Create /
  // Unlinked / Needs link review / On file). The parent claimant chip
  // ("Creates 1 · 1 unlinked") is a rollup; this gives the row-level
  // detail so reviewers can tell which row will load and which is a no-op.
  // Records the loader will drop or collapse are filtered out upstream in
  // DocumentReviewTab — they never reach this nested table.
  const isReimbursementSchema = schema === TargetSchema.REIMBURSEMENT;
  const isClaimItemSchema = schema === TargetSchema.CLAIM_ITEM;
  const showReimbursementColumn =
    isReimbursementSchema &&
    !!getReimbursementRowForRecord &&
    records.some((r) => getReimbursementRowForRecord(r) !== undefined);
  // Claim-item nested table (under an expanded claimant on a claims doc) gets
  // the same per-row "What happens" chip as the flat Claims tab: Insert / On
  // file, backed by the shared natural-key dedup. AB#4456.
  const showClaimItemColumn =
    isClaimItemSchema && !!getClaimItemRowForRecord && records.some((r) => getClaimItemRowForRecord(r) !== undefined);

  const rowStateColumn: CompactTableColumn<ExtractedRecord & { id: string }> | null = showReimbursementColumn
    ? {
        key: "__row_state__",
        label: "What Happens",
        width: "144px",
        align: "left",
        render: (record) => <ReimbursementRowStateChip forecast={getReimbursementRowForRecord?.(record)} />,
      }
    : showClaimItemColumn
      ? {
          key: "__row_state__",
          label: "What Happens",
          width: "144px",
          align: "left",
          render: (record) => <ClaimItemRowStateChip forecast={getClaimItemRowForRecord?.(record)} />,
        }
      : null;

  const columns: CompactTableColumn<ExtractedRecord & { id: string }>[] = rowStateColumn
    ? [rowStateColumn, ...dynamicColumns]
    : dynamicColumns;

  const getRowSx = showReimbursementColumn
    ? (record: ExtractedRecord & { id: string }) => {
        const forecast = getReimbursementRowForRecord?.(record);
        if (!forecast?.is_duplicate_on_file) return {};
        return { opacity: 0.55 };
      }
    : showClaimItemColumn
      ? (record: ExtractedRecord & { id: string }) => {
          const forecast = getClaimItemRowForRecord?.(record);
          if (forecast?.operation === "update" || forecast?.is_duplicate_on_file) return { opacity: 0.55 };
          return {};
        }
      : undefined;

  // For reimbursement rows, expand reveals line-item details (provider, svc
  // dates, per-line amounts, adjustments). The reimbursement record itself
  // has no other meaningful nested content here, so the chevron only
  // appears when there are line items to show. AB#4455.
  const expandRowForLineItems =
    isReimbursementSchema && getReimbursementRowForRecord
      ? (record: ExtractedRecord & { id: string }) => renderLineItemsExpand(getReimbursementRowForRecord(record))
      : undefined;
  const isRowExpandableForLineItems =
    isReimbursementSchema && getReimbursementRowForRecord
      ? (record: ExtractedRecord & { id: string }) => {
          const forecast = getReimbursementRowForRecord(record);
          return !!forecast?.line_items && forecast.line_items.length > 0;
        }
      : undefined;

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
        {SCHEMA_LABELS[schema]} ({records.length})
      </Typography>
      <Box sx={{ mt: 0.75 }}>
        <CompactTable
          data={rows}
          columns={columns}
          rowsPerPage={5}
          showHeader
          fitContent
          getRowSx={getRowSx}
          expandRow={expandRowForLineItems}
          isExpandable={isRowExpandableForLineItems}
        />
      </Box>
    </Box>
  );
};
